import { Fragment, useState } from "react";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
  OnboardingV2AnalyticsEvent,
  OnboardingV2AnalyticsPageView,
} from "@/constants/analytics/Analytics";
import {
  MedicalReviewState,
  OnboardingStepName,
  OrderedOnboardingSteps,
} from "@/constants/Onboarding";
import { OnboardingV2TestID } from "@/constants/TestID";
import useOnboardingMedicalReviewHandler from "@/hooks/onboarding/useOnboardingMedicalReviewHandler";
import useOnboardingStepHandler from "@/hooks/onboarding/useOnboardingStepHandler";
import { useAnalytics } from "@/services/Analytics";
import { useAppSelector } from "@/store/hooks";
import {
  selectOnboardingCurrentStepIndex,
  selectOnboardingIsMedicalReviewCleared,
  selectOnboardingMedicalReviewState,
} from "@/store/reducers/onboarding";
import { ActiveOnboardingCard } from "@/ui/components/onboarding/v2/cards/ActiveOnboardingCard";
import { MedicalReviewCard } from "@/ui/components/onboarding/v2/cards/MedicalReviewCard";
import { OnboardingCollapsedStepCard } from "@/ui/components/onboarding/v2/cards/OnboardingCollapsedStepCard";
import { ExitOnboardingBottomSheetModal } from "@/ui/components/onboarding/v2/ExitOnboardingBottomSheetModal";
import { OnboardingContentHeader } from "@/ui/components/onboarding/v2/OnboardingContentHeader";
import {
  MedicalReviewGateThreshold,
  OnboardingDevControls,
} from "@/ui/components/onboarding/v2/OnboardingDevControls";
import { Box, ScrollView, VStack } from "@/ui/layout";
import { AnimatedSection } from "@/ui/layout/animated/AnimatedSection";
import { FadeInUpDelay } from "@/ui/layout/animated/FadeInUpView";
import { LayoutToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface OnboardingContentProps {
  shouldAnimateEntrance?: boolean;
}

const OnboardingContent = ({
  shouldAnimateEntrance = true,
}: OnboardingContentProps) => {
  const { track } = useAnalytics();
  const [isExitModalOpen, setIsExitModalOpen] = useState<boolean>(false);
  const currentStepIndex = useAppSelector(selectOnboardingCurrentStepIndex);
  const isMedicalReviewCleared = useAppSelector(
    selectOnboardingIsMedicalReviewCleared,
  );
  const medicalReviewState = useAppSelector(selectOnboardingMedicalReviewState);
  const { onStepActionPress } = useOnboardingStepHandler();
  const { onMedicalReviewActionPress } = useOnboardingMedicalReviewHandler();

  const isMedicalReviewVisible =
    currentStepIndex >= MedicalReviewGateThreshold && !isMedicalReviewCleared;
  const effectiveReviewState = medicalReviewState ?? MedicalReviewState.Pending;

  const headerDelay = shouldAnimateEntrance ? FadeInUpDelay.None : undefined;
  const contentDelay = shouldAnimateEntrance
    ? FadeInUpDelay.Default
    : undefined;

  const handleClosePress = () => {
    track(OnboardingV2AnalyticsEvent.ClosePressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.Source]: OnboardingV2AnalyticsPageView.Modal,
    });
    setIsExitModalOpen(true);
  };

  return (
    <>
      <Box width="100%">
        <AnimatedSection delay={headerDelay}>
          <OnboardingContentHeader
            currentStepIndex={currentStepIndex}
            onClosePress={handleClosePress}
            testID={OnboardingV2TestID.Content}
          />
        </AnimatedSection>
      </Box>
      <ScrollView contentContainerStyle={{ paddingBottom: 40 }} width="100%">
        <AnimatedSection delay={contentDelay}>
          <VStack
            gap={8}
            paddingHorizontal={LayoutToken.ContentPaddingHorizontal}
            paddingTop={16}
            width="100%"
          >
            <OnboardingCollapsedStepCard
              state="complete"
              stepName={OnboardingStepName.Initial}
              testID={joinTestIDs(OnboardingV2TestID.Content, "initial")}
            />

            {
              // eslint-disable-next-line max-params -- mapping over array with index parameter
              OrderedOnboardingSteps.map((stepName, stepIndex) => {
                const cardTestID = joinTestIDs(
                  OnboardingV2TestID.Content,
                  stepName,
                );
                const isComplete = stepIndex < currentStepIndex;

                if (
                  stepIndex === MedicalReviewGateThreshold &&
                  isMedicalReviewVisible
                ) {
                  return (
                    <Fragment key={stepName}>
                      <MedicalReviewCard
                        onActionPress={() =>
                          onMedicalReviewActionPress(effectiveReviewState)
                        }
                        state={effectiveReviewState}
                        testID={joinTestIDs(
                          OnboardingV2TestID.Content,
                          "medical-review",
                        )}
                      />
                      <OnboardingCollapsedStepCard
                        state="pending"
                        stepName={stepName}
                        testID={cardTestID}
                      />
                    </Fragment>
                  );
                }

                if (stepIndex === currentStepIndex && !isMedicalReviewVisible) {
                  return (
                    <ActiveOnboardingCard
                      key={stepName}
                      onActionPress={() => onStepActionPress(stepName)}
                      stepName={stepName}
                      testID={cardTestID}
                    />
                  );
                }

                return (
                  <OnboardingCollapsedStepCard
                    key={stepName}
                    state={isComplete ? "complete" : "pending"}
                    stepName={stepName}
                    testID={cardTestID}
                  />
                );
              })
            }
            <OnboardingDevControls />
          </VStack>
        </AnimatedSection>
      </ScrollView>
      <ExitOnboardingBottomSheetModal
        isOpen={isExitModalOpen}
        onDismiss={() => setIsExitModalOpen(false)}
      />
    </>
  );
};

export { OnboardingContent };
